/**
 * El directorio de proveedores: la agenda a la que se manda un RFQ.
 *
 * Es la otra mitad de la cola de altas. La cola decide quien entra; esto es lo
 * que hay dentro, y hasta ahora solo se podia mirar por SQL.
 *
 * ===========================================================================
 * SOLO LECTURA, Y NO ES UNA FASE PENDIENTE
 *
 * Aqui no se edita ni se da de alta. Un proveedor entra por `/apply` y su cola
 * de revision, que es donde estan escritas las reglas --se crea el tercero, su
 * contacto y sus especialidades, y nada entra sin aprobacion humana--. Un
 * segundo camino de escritura duplicaria esas reglas en un sitio donde nadie
 * las buscaria.
 *
 * ===========================================================================
 * LO QUE HAY QUE MIRAR ES `puede_recibir`
 *
 * Un proveedor sin ningun contacto con correo esta en la agenda y NO recibe un
 * RFQ. En una lista corriente se ve igual que los demas, y la pregunta "por que
 * este no me sale al pedir precio" no tiene respuesta visible. Por eso lleva
 * marca propia y se puede filtrar por ella: es el unico defecto de una ficha
 * que se nota tarde.
 */

import type { ProveedorDirectorio } from '../api';
import { el, literal } from '../dom';
import type { Traductor } from '../i18n';

export interface OpcionesDirectorio {
  readonly t: Traductor;
  readonly proveedores: readonly ProveedorDirectorio[];
  readonly q: string;
  readonly hayMas: boolean;
  readonly cargando: boolean;
  /** El id del proveedor desplegado, o `null`. Uno cada vez: la ficha completa
   *  ocupa mucho y con todas abiertas la lista deja de ser una lista. */
  readonly abierto: string | null;
  readonly onBuscar: (q: string) => void;
  readonly onAbrir: (id: string | null) => void;
  readonly onIrAAltas: () => void;
}

export function pintarDirectorio(o: OpcionesDirectorio): HTMLElement {
  const { t } = o;

  const buscador = el('input', {
    clase: 'control',
    attrs: {
      id: 'directorio-q',
      type: 'search',
      value: o.q,
      placeholder: t('proveedores.buscarPlaceholder'),
      autocapitalize: 'none',
      spellcheck: 'false',
    },
  });

  const form = el('form', { clase: 'directorio-buscador' }, [
    el('label', {
      clase: 'etiqueta',
      texto: t('proveedores.buscar'),
      attrs: { for: 'directorio-q' },
    }),
    buscador,
    el('button', {
      clase: 'boton boton-primario',
      texto: t('proveedores.buscarAccion'),
      attrs: { type: 'submit' },
    }),
  ]);

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    o.onBuscar(buscador.value);
  });

  return el('div', { clase: 'zona-proveedores' }, [
    el('header', { clase: 'zona-cabecera' }, [
      el('h2', { texto: t('proveedores.directorio.titulo') }),
      el('p', { clase: 'tenue', texto: t('proveedores.directorio.intro') }),
    ]),

    el('section', { clase: 'panel' }, [
      form,

      o.cargando
        ? el('p', { clase: 'tenue', texto: t('app.cargando') })
        : o.proveedores.length === 0
          ? el('div', { clase: 'vacio' }, [
              el('p', {
                texto:
                  o.q.trim() === ''
                    ? t('proveedores.vacio')
                    : t('proveedores.vacioBusqueda', { q: o.q.trim() }),
              }),
              el('p', { clase: 'tenue', texto: t('proveedores.vacioComo') }),
              el('button', {
                clase: 'boton boton-plano',
                texto: t('proveedores.irAAltas'),
                attrs: { type: 'button' },
                onClick: o.onIrAAltas,
              }),
            ])
          : el('div', {}, [
              el(
                'ul',
                { clase: 'lista-proveedores' },
                o.proveedores.map((p) => ficha(o, p)),
              ),
              // Una lista recortada y una completa se ven igual. Se dice.
              o.hayMas
                ? el('p', { clase: 'tenue', texto: t('proveedores.hayMas') })
                : null,
            ]),
    ]),
  ]);
}

function ficha(o: OpcionesDirectorio, p: ProveedorDirectorio): HTMLElement {
  const { t } = o;
  const abierto = o.abierto === p.id;

  return el('li', { clase: `proveedor ${abierto ? 'proveedor-abierto' : ''}` }, [
    el('div', { clase: 'proveedor-cabecera' }, [
      // El nombre del tercero es literal: llego en un correo y puede estar en
      // cualquier alfabeto.
      literal(p.nombre, 'literal proveedor-nombre'),
      p.pais === null ? null : el('span', { clase: 'tenue', texto: p.pais }),
      p.identificado
        ? null
        : // [Tema 6] La empresa no se pudo identificar del correo. Se marca; no
          // se esconde ni se inventa.
          el('span', {
            clase: 'badge badge-atencion',
            texto: t('proveedores.noIdentificado'),
          }),
      p.puede_recibir
        ? null
        : el('span', {
            clase: 'badge badge-atencion',
            texto: t('proveedores.sinCorreo'),
            attrs: { title: t('proveedores.sinCorreoAyuda') },
          }),
      el('span', {
        clase: 'tenue',
        texto: t('proveedores.resumen', {
          contactos: String(p.contactos.length),
          especialidades: String(p.especialidades.length),
          sheets: String(p.rate_sheets),
        }),
      }),
      el('button', {
        clase: 'boton boton-plano boton-abrir',
        texto: abierto ? t('proveedores.cerrar') : t('proveedores.ver'),
        attrs: { type: 'button', 'aria-expanded': abierto ? 'true' : 'false' },
        onClick: () => {
          o.onAbrir(abierto ? null : p.id);
        },
      }),
    ]),

    abierto ? detalle(o, p) : null,
  ]);
}

function detalle(o: OpcionesDirectorio, p: ProveedorDirectorio): HTMLElement {
  const { t } = o;

  return el('div', { clase: 'proveedor-detalle' }, [
    el('h4', { texto: t('proveedores.contactos') }),
    p.contactos.length === 0
      ? el('p', { clase: 'tenue', texto: t('proveedores.sinContactos') })
      : el(
          'ul',
          { clase: 'lista-contactos' },
          p.contactos.map((c) =>
            el('li', {}, [
              literal(c.nombre) ??
                el('span', { clase: 'tenue', texto: t('proveedores.sinNombre') }),
              c.email === null
                ? el('span', { clase: 'tenue', texto: t('proveedores.sinEmail') })
                : el('code', { texto: c.email }),
              c.idioma === null
                ? null
                : el('span', { clase: 'badge', texto: c.idioma.toUpperCase() }),
              c.departamento === null ? null : literal(c.departamento, 'tenue'),
              c.telefono === null
                ? null
                : el('span', { clase: 'tenue', texto: c.telefono }),
              // A quien se le escribe de verdad al pedir precio.
              c.recibe_rfq
                ? el('span', {
                    clase: 'badge badge-primario',
                    texto: t('proveedores.recibeRfq'),
                  })
                : null,
            ]),
          ),
        ),

    el('h4', { texto: t('proveedores.especialidades') }),
    p.especialidades.length === 0
      ? el('p', {
          clase: 'tenue',
          // Sin especialidad no se propone en el RFQ de ninguna ruta. Es la
          // segunda ficha incompleta que se nota tarde.
          texto: t('proveedores.sinEspecialidades'),
        })
      : el(
          'ul',
          { clase: 'lista-especialidades' },
          p.especialidades.map((e) =>
            el('li', {}, [
              el('span', { clase: 'chip', texto: t(`modo.${e.modo}`) }),
              el('span', { texto: `${e.origen} → ${e.destino}` }),
              // El texto con el que el proveedor declaro su alcance, tal cual.
              literal(e.alcance_literal, 'literal tenue'),
            ]),
          ),
        ),
  ]);
}
